import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import "../../style/addAndEditEmpMan.css";
import {
  createEmployee,
  updateEmployee,
} from "../../services/employe-management-api";

function AddAndEditEmpMan({ show, onHide, employee, onSave }) {
  const isEdit = !!employee;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "",
  });
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const roleDropdownRef = useRef(null);

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name || "",
        email: employee.email || "",
        role: employee.role?.toLowerCase() || "",
      });
    } else {
      setFormData({ name: "", email: "", role: "" });
    }
  }, [employee, show]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target)
      ) {
        setShowRoleDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "employee", label: "Employee" },
  ];

  const getRoleLabel = () => {
    const option = roleOptions.find((opt) => opt.value === formData.role);
    return option ? option.label : "Select Role";
  };

  const handleRoleSelect = (value) => {
    setFormData({ ...formData, role: value });
    setShowRoleDropdown(false);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.role) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        admin_role: formData.role.toUpperCase(),
      };

      if (isEdit) {
        await updateEmployee(employee.id, payload);
        toast.success("Employee updated successfully");
      } else {
        await createEmployee(payload);
        toast.success("Employee created successfully");
      }
      onSave();
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(
        error.response?.data?.message || "Failed to save employee"
      );
    }
  };

  if (!show) return null;

  return (
    <div className="emp-modal-overlay" onClick={onHide}>
      <div className="emp-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="emp-modal-header">
          <h3 className="emp-modal-title">
            {isEdit ? "Edit Employee" : "Add New Employee"}
          </h3>
          <p className="emp-modal-subtitle">
            Add new employee by filling details below
          </p>
        </div>

        <div style={{ padding: "32px" }}>
          <div className="emp-form">
            <div className="emp-form-group">
              <label className="emp-form-label">Employee Name</label>
              <input
                type="text"
                className="emp-form-input"
                placeholder="Enter full name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="emp-form-group">
              <label className="emp-form-label">Email Address</label>
              <input
                type="email"
                className="emp-form-input"
                placeholder="Enter employee email address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className="emp-form-group">
              <label className="emp-form-label">Role</label>
              <div className="emp-role-dropdown" ref={roleDropdownRef}>
                <button
                  type="button"
                  className="emp-role-dropdown-btn"
                  onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                >
                  {getRoleLabel()}
                  {showRoleDropdown ? (
                    <ChevronUp size={16} className="emp-role-dropdown-arrow" />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="emp-role-dropdown-arrow"
                    />
                  )}
                </button>
                <div
                  className={`emp-role-dropdown-content ${
                    showRoleDropdown ? "show" : ""
                  }`}
                >
                  {roleOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`emp-role-dropdown-item ${
                        formData.role === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleRoleSelect(option.value)}
                    >
                      <Check size={16} className="emp-role-dropdown-check" />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="emp-modal-footer">
            <button className="emp-cancel-btn" onClick={onHide}>
              Cancel
            </button>
            <button className="emp-save-btn" onClick={handleSubmit}>
              Save Employee
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddAndEditEmpMan;
